import type { LapDoc } from './timing.ts'

/**
 * What a car burns, from what went into it.
 *
 * Fuel goes in to the top, so a fill measures what the car used since the fill
 * before it — over however many sessions that spans. Fuel goes into a car only
 * at the fuel station, and every fill there is on the fuel list, so the laps
 * between two fills are every lap the car completed between them: ten at the
 * end of Friday and twenty on Saturday morning are thirty laps on one tank.
 * Before the first fill of the event the car is taken to have been full,
 * filled in the workshop; a car that arrived otherwise is filled before it
 * runs, and that fill measures nothing because no laps came before it.
 *
 * Two things spoil the measure, and the team says which:
 *
 * - A fill not to the top — ten litres to see out qualifying. It does not
 *   measure what was used, and neither does the fill after it, which tops up
 *   what was burnt plus what the short fill left out. Together they do: the
 *   two fills' litres are what the car used across both stretches. So a short
 *   fill joins its stretch to the next rather than discarding either.
 * - A stretch that is not representative — a crash, a long stop with the
 *   engine running. Left out of the rate, but still shown.
 *
 * Then a yellow: behind a full-course yellow a car burns far less, so a
 * stretch's litres are green laps at one rate plus yellow laps at another.
 * Every counted stretch is one such sum, and the two rates are the pair that
 * best fits all of them. That needs stretches whose share of yellow differs;
 * where they do not, only the green rate is given, and from green running.
 *
 * Pit in- and out-laps count as racing laps: they are driven hard.
 */

/** A session of the team's at the event, in the order they ran. */
export interface BurnSession {
  id: string
  start: Date
}

/** One completed lap of the car. */
export interface BurnLap {
  session: string
  lap: number
  completedAt: Date | null
  /** How much of it was run under a full-course yellow, 0 to 1, by sector. */
  fcy: number
  /** Its time, for pace. */
  lapMs?: number | null
  /** An in- or out-lap: fuel like any other, but no use for pace. */
  pit?: boolean
}

/** One fill, and where the fuel job placed it, if it could. */
export interface BurnFill {
  id: string
  at: Date
  litres: number
  /** The session and completed laps the fuel job matched it to. */
  place?: { session: string; lap: number }
}

/** What the team has said about a fill. Kept per fill id. */
export interface FuelMark {
  /** It did not fill the tank to the top. */
  partial?: boolean
  /** The stretch it ends is not to be counted in the rate. */
  excluded?: boolean
}

export type FuelMarks = Record<string, FuelMark>

/**
 * One refuelling: the fills that went in at one place. A stop's fuel is
 * sometimes booked as two rows, and both together are what it took.
 */
export interface Refuel {
  fills: BurnFill[]
  at: Date
  litres: number
  /** Laps the car had completed across the event when it went in. */
  position: number
  partial: boolean
}

/** From one full tank to the next. */
export interface FuelStretch {
  /** The last fill's id: where marks about the stretch are kept. */
  key: string
  /** The refuels ending it — more than one where short fills were joined. */
  refuels: Refuel[]
  /** No fill before it: measured from a car that started the event full. */
  fromStart: boolean
  laps: number
  fcyLaps: number
  litres: number
  excluded: boolean
  /** Litres per lap, all laps alike. Null where no laps were run. */
  rate: number | null
}

/** The fuel in the car now, since the last full tank. */
export interface OpenStretch {
  laps: number
  fcyLaps: number
  /** Litres put in since by short fills, which the next full fill will not see. */
  shortLitres: number
  since: Date | null
  /** Where it starts in the event's count of laps. */
  from: number
  /** The short fills since. */
  refuels: Refuel[]
}

export interface FuelBurn {
  stretches: FuelStretch[]
  open: OpenStretch
  rates: BurnRates
  /** Laps completed across the event. */
  total: number
  /** Where each session starts in that count. */
  sessionStarts: Record<string, number>
  /** Laps and yellow laps between two places in the count. */
  span: (from: number, to: number) => { laps: number; fcyLaps: number }
}

export interface BurnRates {
  /** Litres per green lap. */
  green: number | null
  /** Litres per lap under a full-course yellow. */
  fcy: number | null
  /** The stretches and laps the rates are from. */
  stretches: number
  laps: number
  fcyLaps: number
  /** Why a rate is missing, or what it is from, where that needs saying. */
  note: string | null
}

/** Where the fuel job placed a fill in one session. */
export interface FuelPlacement {
  session: string
  /** When that session started. */
  start: Date
  kind: 'stop' | 'after' | 'before' | 'unmatched'
  lap?: number
}

/**
 * Which of the fuel job's placements of a fill to believe.
 *
 * It places a fill in every session it is near, so a fill-up after
 * qualifying is also "after" the practice that finished an hour before. At a
 * stop is certain. Otherwise the session that had most recently started by
 * the time of the fill: the car's last running before it.
 */
export function placeFill(at: Date, placements: readonly FuelPlacement[]): BurnFill['place'] {
  const usable = placements.filter((p) => p.lap != null && p.kind !== 'unmatched')
  const stop = usable.find((p) => p.kind === 'stop')
  if (stop) return { session: stop.session, lap: stop.lap! }
  const t = at.getTime()
  const started = usable.filter((p) => p.start.getTime() <= t).sort((a, b) => b.start.getTime() - a.start.getTime())
  const pick = started[0] ?? usable.sort((a, b) => a.start.getTime() - b.start.getTime())[0]
  return pick ? { session: pick.session, lap: pick.lap! } : undefined
}

/** The share of a lap run under a full-course yellow. Slow zones are local and short, so not. */
export function lapFcyShare(lap: Pick<LapDoc, 's1Fcy' | 's2Fcy' | 's3Fcy'>): number {
  return ((lap.s1Fcy ? 1 : 0) + (lap.s2Fcy ? 1 : 0) + (lap.s3Fcy ? 1 : 0)) / 3
}

export function fuelBurn(
  sessions: readonly BurnSession[],
  laps: readonly BurnLap[],
  fills: readonly BurnFill[],
  marks: FuelMarks = {},
): FuelBurn {
  const ordered = [...sessions].sort((a, b) => a.start.getTime() - b.start.getTime())
  const known = new Set(ordered.map((s) => s.id))

  // Each session's length in laps, and where it starts in the event's count.
  const last = new Map<string, number>()
  for (const l of laps) last.set(l.session, Math.max(last.get(l.session) ?? 0, l.lap))
  for (const f of fills) {
    if (f.place && known.has(f.place.session)) {
      last.set(f.place.session, Math.max(last.get(f.place.session) ?? 0, f.place.lap))
    }
  }
  const offset = new Map<string, number>()
  let total = 0
  for (const s of ordered) {
    offset.set(s.id, total)
    total += last.get(s.id) ?? 0
  }

  // The yellow in each of the event's laps, by its place in the count.
  const fcyAt = new Map<number, number>()
  for (const l of laps) {
    const o = offset.get(l.session)
    if (o != null && l.lap > 0) fcyAt.set(o + l.lap, l.fcy)
  }
  const fcyBetween = (from: number, to: number) => {
    let n = 0
    for (let p = from + 1; p <= to; p++) n += fcyAt.get(p) ?? 0
    return n
  }

  // Where the fuel job placed it; otherwise the laps completed by then, in
  // the last session to have started.
  const position = (f: BurnFill): number => {
    if (f.place && known.has(f.place.session)) return offset.get(f.place.session)! + f.place.lap
    const t = f.at.getTime()
    const s = ordered.filter((x) => x.start.getTime() <= t).pop()
    if (!s) return 0
    let done = 0
    for (const l of laps) {
      if (l.session === s.id && l.completedAt && l.completedAt.getTime() <= t) done = Math.max(done, l.lap)
    }
    return offset.get(s.id)! + done
  }

  const placed = fills
    .map((f) => ({ f, pos: position(f) }))
    .sort((a, b) => a.f.at.getTime() - b.f.at.getTime() || a.f.id.localeCompare(b.f.id))

  const refuels: Refuel[] = []
  for (const { f, pos } of placed) {
    const prev = refuels[refuels.length - 1]
    // A fill at a later time but an earlier lap is a placement disagreeing
    // with the clock; it is held where the car had got to.
    const at = Math.max(pos, prev?.position ?? 0)
    if (prev && prev.position === at) {
      prev.fills.push(f)
      prev.litres += f.litres
      prev.partial ||= !!marks[f.id]?.partial
      continue
    }
    refuels.push({ fills: [f], at: f.at, litres: f.litres, position: at, partial: !!marks[f.id]?.partial })
  }

  const stretches: FuelStretch[] = []
  let from = 0
  let pending: Refuel[] = []
  for (const r of refuels) {
    pending.push(r)
    if (r.partial) continue
    const lapsRun = r.position - from
    const litres = pending.reduce((s, x) => s + x.litres, 0)
    const ids = pending.flatMap((x) => x.fills.map((f) => f.id))
    stretches.push({
      key: ids[ids.length - 1]!,
      refuels: pending,
      fromStart: stretches.length === 0,
      laps: lapsRun,
      fcyLaps: fcyBetween(from, r.position),
      litres,
      excluded: ids.some((id) => marks[id]?.excluded),
      rate: lapsRun > 0 ? litres / lapsRun : null,
    })
    from = r.position
    pending = []
  }

  const open: OpenStretch = {
    laps: total - from,
    fcyLaps: fcyBetween(from, total),
    shortLitres: pending.reduce((s, x) => s + x.litres, 0),
    since: stretches.length > 0 ? stretches[stretches.length - 1]!.refuels.at(-1)!.at : null,
    from,
    refuels: pending,
  }

  return {
    stretches,
    open,
    rates: burnRates(stretches),
    total,
    sessionStarts: Object.fromEntries(offset),
    span: (a, b) => ({ laps: Math.max(0, b - a), fcyLaps: fcyBetween(a, b) }),
  }
}

/** A stretch with less yellow than this is green running. */
const greenShare = 0.02

/**
 * The green and yellow rates that best fit the counted stretches.
 *
 * Least squares on litres = green laps × green rate + yellow laps × yellow
 * rate. The yellow rate is believed only where the stretches differ enough in
 * how much yellow they had to tell the two apart, and where it comes out
 * between nothing and the green rate.
 */
export function burnRates(stretches: readonly FuelStretch[]): BurnRates {
  const use = stretches.filter((s) => !s.excluded && s.laps > 0)
  const laps = use.reduce((n, s) => n + s.laps, 0)
  const fcyLaps = use.reduce((n, s) => n + s.fcyLaps, 0)
  const base = { stretches: use.length, laps, fcyLaps }
  if (use.length === 0) return { green: null, fcy: null, ...base, note: 'No stretch between two full tanks yet' }

  let gg = 0
  let gy = 0
  let yy = 0
  let gl = 0
  let yl = 0
  for (const s of use) {
    const g = s.laps - s.fcyLaps
    gg += g * g
    gy += g * s.fcyLaps
    yy += s.fcyLaps * s.fcyLaps
    gl += g * s.litres
    yl += s.fcyLaps * s.litres
  }
  const withYellow = use.filter((s) => s.fcyLaps >= 1).length
  const det = gg * yy - gy * gy
  if (withYellow >= 1 && use.length >= 2 && fcyLaps >= 2 && det > 0.05 * gg * yy) {
    const green = (gl * yy - yl * gy) / det
    const fcy = (yl * gg - gl * gy) / det
    if (green > 0 && fcy > 0 && fcy < green) return { green, fcy, ...base, note: null }
  }

  // Not enough to separate them: green from the green running alone.
  const green = use.filter((s) => s.fcyLaps / s.laps < greenShare)
  if (green.length > 0) {
    const l = green.reduce((n, s) => n + s.litres, 0)
    const n = green.reduce((n, s) => n + s.laps, 0)
    return {
      green: l / n,
      fcy: null,
      stretches: green.length,
      laps: n,
      fcyLaps: green.reduce((n, s) => n + s.fcyLaps, 0),
      note: fcyLaps >= 1 ? 'Too little yellow to tell its rate apart' : null,
    }
  }
  // Yellow in every stretch, and not enough difference to separate it: a
  // yellow lap taken at a fixed share of a green one. Measured at Portimão it
  // was 0.77; a strategy needs a green rate, and dividing by the laps alone
  // would count the yellow as green and understate it.
  const litres = use.reduce((n, s) => n + s.litres, 0)
  const assumed = litres / (laps - fcyLaps + assumedFcyShare * fcyLaps)
  return {
    green: assumed,
    fcy: null,
    ...base,
    note: `Yellow in every stretch, so a yellow lap is taken as ${assumedFcyShare * 100}% of a green one`,
  }
}

/** What a lap under a full-course yellow burns against a green one, where it cannot be measured. */
export const assumedFcyShare = 0.75

/** What the rates say a stretch should have taken. */
export function expectedLitres(s: Pick<FuelStretch, 'laps' | 'fcyLaps'>, rates: BurnRates): number | null {
  if (rates.green == null) return null
  // Without a yellow rate, a stretch with a lap or more of yellow has no
  // expectation; a sector or two is near enough green.
  if (rates.fcy == null && s.fcyLaps >= 1) return null
  return (s.laps - s.fcyLaps) * rates.green + s.fcyLaps * (rates.fcy ?? rates.green)
}
